/**
 * Utilidades para el módulo de Facturación de Athenea
 */

/**
 * Formatea un NIF/CIF eliminando guiones y espacios, y añadiendo prefijo de país si no existe.
 * Ejemplo: "B-12345678" → "ESB12345678"
 *
 * @param taxId - El NIF/CIF/NIE a formatear
 * @param countryCode - Código de país (default: 'ES')
 * @returns NIF/CIF formateado
 */
export function formatTaxId(taxId: string | null | undefined, countryCode: string = 'ES'): string {
  if (!taxId) return '';

  // Eliminar guiones, espacios y caracteres no alfanuméricos
  const cleaned = taxId.replace(/[-\s]/g, '').toUpperCase();

  // Si ya empieza con código de país (2 letras), retornar limpio
  if (/^[A-Z]{2}/.test(cleaned)) {
    return cleaned;
  }

  // Agregar prefijo de país si no existe
  return `${countryCode}${cleaned}`;
}

/**
 * Mapa de códigos internos de métodos de pago a etiquetas legibles en español
 */
export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  transfer: 'Transferencia bancaria',
  cash: 'Efectivo',
  card: 'Tarjeta de crédito/débito',
  direct_debit: 'Domiciliación bancaria',
  check: 'Cheque',
  confirming: 'Confirming',
  promissory_note: 'Pagaré',
  other: 'Otro',
};

/**
 * Obtiene la etiqueta legible de un método de pago
 *
 * @param method - Código interno del método de pago (ej: 'transfer')
 * @returns Etiqueta legible (ej: 'Transferencia bancaria')
 */
export function getPaymentMethodLabel(method: string | null | undefined): string {
  if (!method) return '';
  return PAYMENT_METHOD_LABELS[method] || method;
}

/**
 * Formatea un IBAN añadiendo espacios cada 4 caracteres para mejor legibilidad
 * Ejemplo: "ES4100492313602514261409" → "ES41 0049 2313 6025 1426 1409"
 *
 * @param iban - IBAN sin formatear
 * @returns IBAN formateado con espacios
 */
export function formatIBAN(iban: string | null | undefined): string {
  if (!iban) return '';

  // Eliminar espacios existentes y convertir a mayúsculas
  const cleaned = iban.replace(/\s/g, '').toUpperCase();

  // Añadir espacio cada 4 caracteres
  return cleaned.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Valida si un NIF/CIF español tiene formato correcto
 *
 * @param taxId - NIF/CIF a validar
 * @returns true si el formato es válido
 */
export function isValidSpanishTaxId(taxId: string | null | undefined): boolean {
  if (!taxId) return false;

  // Eliminar prefijo ES si existe
  const cleaned = taxId.replace(/^ES/i, '').replace(/[-\s]/g, '').toUpperCase();

  // NIF personal: 8 dígitos + letra
  const nifPattern = /^[0-9]{8}[A-Z]$/;

  // NIE extranjero: X/Y/Z + 7 dígitos + letra
  const niePattern = /^[XYZ][0-9]{7}[A-Z]$/;

  // CIF empresa: letra + 7 dígitos + dígito/letra
  const cifPattern = /^[ABCDEFGHJKLMNPQRSUVW][0-9]{7}[0-9A-J]$/;

  return nifPattern.test(cleaned) || niePattern.test(cleaned) || cifPattern.test(cleaned);
}

/**
 * Estados de factura con sus etiquetas y colores
 */
export const INVOICE_STATUS_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  draft: { label: 'Borrador', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  issued: { label: 'Emitida', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  sent: { label: 'Enviada', color: 'text-indigo-700', bgColor: 'bg-indigo-100' },
  paid: { label: 'Cobrada', color: 'text-green-700', bgColor: 'bg-green-100' },
  partial: { label: 'Cobro parcial', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  cancelled: { label: 'Anulada', color: 'text-red-700', bgColor: 'bg-red-100' },
  rectified: { label: 'Rectificada', color: 'text-purple-700', bgColor: 'bg-purple-100' },
};

/**
 * Tipos de factura con sus etiquetas
 */
export const INVOICE_TYPE_LABELS: Record<string, string> = {
  standard: 'Factura',
  rectificativa: 'Factura Rectificativa',
  proforma: 'Factura Proforma',
};

/**
 * Obtiene la etiqueta de un tipo de factura
 */
export function getInvoiceTypeLabel(type: string | null | undefined): string {
  if (!type) return 'Factura';
  return INVOICE_TYPE_LABELS[type] || type;
}

/**
 * Formatea un importe como moneda EUR
 */
export function formatCurrency(amount: number | null | undefined): string {
  if (amount == null) return '0,00 €';
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

/**
 * Formatea un número con decimales al estilo español
 */
export function formatNumber(value: number | null | undefined, decimals: number = 2): string {
  if (value == null) return '0';
  return new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}
